import { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  Stack
} from '@mui/material';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import AdpopcornDashboard from '../../../components/pointhome/adpopcorn/AdpopcornDashboard';
import AdpopcornTotalMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornTotalMetricsTable';
import AdpopcornMediationRevenueMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationRevenueMetricsTable';
import AdpopcornMediationDetailMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationDetailMetricsTable';
import AdpopcornPlacementMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornPlacementMetricsTable';
import apiClient from '../../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import {
  AdpopcornTotalMetrics,
  AdpopcornMediationRevenueMetrics,
  AdpopcornMediationDetailMetrics,
  AdpopcornPlacementMetrics
} from '../../../types/adpopcorn.types';

export default function AdPopcorn() {
  const [adpopcornTotalMetrics, setAdpopcornTotalMetrics] = useState<AdpopcornTotalMetrics | null>(null);
  const [adpopcornMediationRevenueMetrics, setAdpopcornMediationRevenueMetrics] = useState<AdpopcornMediationRevenueMetrics | null>(null);
  const [adpopcornMediationDetailMetrics, setAdpopcornMediationDetailMetrics] = useState<AdpopcornMediationDetailMetrics | null>(null);
  const [adpopcornPlacementMetrics, setAdpopcornPlacementMetrics] = useState<AdpopcornPlacementMetrics | null>(null);
  const [thirdPartyId, setThirdPartyId] = useState<number>(-1);
  const [thirdPartyName, setThirdPartyName] = useState<string>('미디에이션 전체');
  const [adpopcornTotalRevenue, setAdpopcornTotalRevenue] = useState<number>(0);
  const [adpopcornPreviousTotalRevenue, setAdpopcornPreviousTotalRevenue] = useState<number>(0);
  const [adpopcornDailyStats, setAdpopcornDailyStats] = useState<{
    date: string;
    revenue: number;
    req: number;
    res: number;
    imp: number;
    clk: number;
  }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const selectedApp = '앱 전체'; // 항상 앱 전체로 고정
  const [activeTab, setActiveTab] = useState<number>(0);
  const [osAnchorEl, setOsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [serviceAnchorEl, setServiceAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState<number>(0);

  const OS_OPTIONS = [
    { label: 'AOS + IOS', value: 'all' },
    { label: 'AOS', value: 'aos' },
    { label: 'IOS', value: 'ios' },
  ];

  const SERVICE_FILTER_OPTIONS = [
    { label: '포인트홈 + 룰렛', value: 0 },
    { label: '포인트홈', value: 1 },
    { label: '룰렛', value: 2 },
  ];

  // 서비스 필터 값을 서비스 이름으로 변환
  const getServiceName = (filterValue: number): string => {
    const serviceMap: Record<number, string> = {
      0: 'all', // 포인트홈 + 룰렛 (전체)
      1: 'pointhome', // 포인트홈
      2: 'pointhome:roulette', // 룰렛
    };
    return serviceMap[filterValue] || 'all';
  };

  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdpopcornTotalMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {}),
      os_type: selectedOs,
      service: getServiceName(selectedServiceFilter)
    };

    apiClient.get(`/api/v1/stat/adpopcorn/pointhome/metrics/total`, {
      params: params
    }).then((response) => {
      setAdpopcornTotalMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate, selectedApp, selectedOs, selectedServiceFilter]);

  const fetchAdpopcornMediationRevenueMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');
    
    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };
    
    apiClient.get(`/api/v1/stat/adpopcorn/pointhome/metrics/mediation`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationRevenueMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp]);

  const fetchAdpopcornMediationDetailMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string | number | undefined> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {}),
      ...(thirdPartyId !== -1 ? { third_party_id: thirdPartyId } : {})
    };
    
    apiClient.get(`/api/v1/stat/adpopcorn/pointhome/metrics/mediation/detail`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationDetailMetrics(response.data);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, thirdPartyId]);

  const fetchAdpopcornPlacementMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
        ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
        : {})
    };

    apiClient.get(`/api/v1/stat/adpopcorn/pointhome/metrics/placement`, {
      params: params
    }).then((response) => {
      setAdpopcornPlacementMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp]);

  useEffect(() => {
    if (initialized && startDate && endDate) {
      // 현재 탭에 맞는 데이터 로드
      if (activeTab === 0) {
        fetchAdpopcornTotalMetrics();
      } else if (activeTab === 1) {
        fetchAdpopcornMediationRevenueMetrics();
      }
      else if (activeTab === 2) {
        fetchAdpopcornMediationDetailMetrics();
      }
      else if (activeTab === 3) {
        fetchAdpopcornPlacementMetrics();
      }
    }
  }, [
    fetchAdpopcornTotalMetrics,
    fetchAdpopcornMediationRevenueMetrics,
    fetchAdpopcornMediationDetailMetrics,
    fetchAdpopcornPlacementMetrics,
    initialized, startDate, endDate, activeTab, thirdPartyId, selectedOs, selectedServiceFilter
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box>
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={() => {
            if (startDate && endDate) {
              // 현재 탭에 맞는 데이터 로드
              if (activeTab === 0) {
                fetchAdpopcornTotalMetrics();
              } else if (activeTab === 1) {
                fetchAdpopcornMediationRevenueMetrics();
              }
            }
          }}
        />
      </Box>
      
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* OS 필터 & 서비스 필터 - 전체 현황 탭에서만 표시 */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={(event) => setOsAnchorEl(event.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    borderColor: '#d0d7e2',
                    borderRadius: 2,
                    textTransform: 'none',
                    color: '#1f2a37',
                    minWidth: 220,
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <LaptopMacOutlinedIcon fontSize="small" />
                    <Stack direction="column" alignItems="flex-start">
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
                        OS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {OS_OPTIONS.find((o) => o.value === selectedOs)?.label ?? 'AOS + IOS'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Button>
                <Button
                  variant="outlined"
                  onClick={(event) => setServiceAnchorEl(event.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    borderColor: '#d0d7e2',
                    borderRadius: 2,
                    textTransform: 'none',
                    color: '#1f2a37',
                    minWidth: 220,
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <EmojiObjectsOutlinedIcon fontSize="small" />
                    <Stack direction="column" alignItems="flex-start">
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
                        서비스
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {SERVICE_FILTER_OPTIONS.find((o) => o.value === selectedServiceFilter)?.label ?? '포인트홈 + 룰렛'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Button>
                <Menu
                  anchorEl={osAnchorEl}
                  open={Boolean(osAnchorEl)}
                  onClose={() => setOsAnchorEl(null)}
                >
                  {OS_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      selected={option.value === selectedOs}
                      onClick={() => {
                        setOsAnchorEl(null);
                        setSelectedOs(option.value);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
                <Menu
                  anchorEl={serviceAnchorEl}
                  open={Boolean(serviceAnchorEl)}
                  onClose={() => setServiceAnchorEl(null)}
                >
                  {SERVICE_FILTER_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      selected={option.value === selectedServiceFilter}
                      onClick={() => {
                        setServiceAnchorEl(null);
                        setSelectedServiceFilter(option.value);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            <AdpopcornDashboard
              dailyStats={adpopcornDailyStats}
              totalRevenue={adpopcornTotalRevenue}
              previousTotalRevenue={adpopcornPreviousTotalRevenue} 
            />

            <Tabs
              value={activeTab}
              onChange={(_, newValue) => {
                setActiveTab(newValue);
                // 탭 변경 시 해당 탭의 데이터 로드
                if (newValue === 0) {
                  fetchAdpopcornTotalMetrics();
                } else if (newValue === 1) {
                  fetchAdpopcornMediationRevenueMetrics();
                }
              }}
              sx={{
                mt: 3
              }}
            >
              <Tab value={0} label="전체 현황" />
              <Tab value={1} label="미디에이션 매출" />
              <Tab value={2} label="미디에이션 상세 지표" />
              <Tab value={3} label="지면별 지표" />
            </Tabs>

            {
              activeTab === 0 && adpopcornTotalMetrics && startDate && endDate && (
                <AdpopcornTotalMetricsTable 
                  data={adpopcornTotalMetrics} 
                  showTopApps={true}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 1 && adpopcornMediationRevenueMetrics && startDate && endDate && (
                <AdpopcornMediationRevenueMetricsTable 
                  data={adpopcornMediationRevenueMetrics}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 2 && adpopcornMediationDetailMetrics && startDate && endDate && (
                <AdpopcornMediationDetailMetricsTable 
                  data={adpopcornMediationDetailMetrics}
                  thirdPartyId={thirdPartyId}
                  thirdPartyName={thirdPartyName}
                  startDate={startDate}
                  endDate={endDate}
                  onThirdPartyIdChange={(id, name) => {
                    setThirdPartyId(id);
                    setThirdPartyName(name);
                  }}
                />
              )
            }
            {
              activeTab === 3 && adpopcornPlacementMetrics && startDate && endDate && (
                <AdpopcornPlacementMetricsTable 
                  data={adpopcornPlacementMetrics}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
          </>
        )}
      </Box>
    </Box>
  );
}
